import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Upload, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import Wordmark from "../components/Wordmark";
import PodcastSubmitForm from "../components/PodcastSubmitForm";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const empty = {
  title: "", director: "", synopsis: "", year: "", runtime: "", genres: "",
  poster: "", backdrop: "", trailer_url: "", watch_url: "", watch_label: "Watch now", contact_email: "",
};

export default function SubmitFilm() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "podcast" ? "podcast" : "film";
  const [f, setF] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const uploadPoster = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, poster: `${BACKEND}${data.url}` }));
      toast.success("Poster uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...f,
        year: f.year ? parseInt(f.year, 10) : null,
        runtime: f.runtime ? parseInt(f.runtime, 10) : null,
        genres: f.genres.split(",").map((g) => g.trim()).filter(Boolean),
      };
      await api.post("/indie/submit", payload);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]";
  const label = "mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]";

  if (done && tab === "film") {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] px-4 py-16">
        <div className="mx-auto max-w-lg text-center" data-testid="submit-success">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--accent)]/20 text-[var(--accent)]"><Check size={32} /></div>
          <h1 className="mt-5 font-head text-3xl text-[var(--text-primary)]">Thanks for submitting!</h1>
          <p className="mt-3 text-sm text-[var(--text-secondary)]">Your film has been sent to our team for review. If it's a fit for the Indie Spotlight, we'll be in touch at the email you provided.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-6 py-3 font-bold uppercase tracking-wide text-[var(--accent-contrast)]" data-testid="submit-back-home">Back to The Dial</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <header className="flex items-center justify-between px-4 py-4 lg:px-14">
        <Link to="/"><Wordmark sizeClass="text-2xl sm:text-3xl" /></Link>
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="submit-back"><ArrowLeft size={16} /> Back</Link>
      </header>

      <main className="mx-auto max-w-2xl px-4 pb-24 lg:px-0">
        <div className="mb-6 inline-flex rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-1" role="tablist" data-testid="submit-tabs">
          {[["film", "Submit a Film"], ["podcast", "Submit a Podcast"]].map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setParams(k === "podcast" ? { tab: "podcast" } : {}, { replace: true })}
              className={`rounded-[var(--card-radius)] px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors ${tab === k ? "bg-[var(--accent)] text-[var(--accent-contrast)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
              data-testid={`submit-tab-${k}`}>{l}</button>
          ))}
        </div>
        <p className="-mt-3 mb-5 text-xs text-[var(--text-secondary)]">New here? <Link to="/creators" className="font-semibold text-[var(--accent-hover)] underline underline-offset-2" data-testid="submit-how-it-works">How it works</Link></p>
        {tab === "podcast" ? (
          <>
            <div className="mb-1 inline-flex items-center gap-2 rounded-full bg-[var(--accent)]/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent-hover)]">For TV &amp; Film Podcasters</div>
            <h1 className="mt-2 font-head text-4xl text-[var(--text-primary)]">Submit your podcast</h1>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">Podcast Spotlight features shows about TV and film. We list your show and link out to where people listen — The Dial never hosts episodes.</p>
            <PodcastSubmitForm />
          </>
        ) : (<>
        <div className="mb-1 inline-flex items-center gap-2 rounded-full bg-[var(--accent)]/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent-hover)]">For Filmmakers</div>
        <h1 className="mt-2 font-head text-4xl text-[var(--text-primary)]">Submit your film</h1>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">Tell us about your independent film and where people can watch it. We review every submission for the Indie Spotlight.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="submit-form">
          <div>
            <label className={label}>Film title *</label>
            <input className={field} value={f.title} onChange={set("title")} required data-testid="submit-title" />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div><label className={label}>Director / filmmaker</label><input className={field} value={f.director} onChange={set("director")} data-testid="submit-director" /></div>
            <div><label className={label}>Contact email *</label><input type="email" className={field} value={f.contact_email} onChange={set("contact_email")} required data-testid="submit-email" /></div>
          </div>
          <div>
            <label className={label}>Synopsis</label>
            <textarea className={`${field} min-h-[110px]`} value={f.synopsis} onChange={set("synopsis")} data-testid="submit-synopsis" />
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <div><label className={label}>Release year</label><input className={field} value={f.year} onChange={set("year")} inputMode="numeric" data-testid="submit-year" /></div>
            <div><label className={label}>Runtime (min)</label><input className={field} value={f.runtime} onChange={set("runtime")} inputMode="numeric" data-testid="submit-runtime" /></div>
            <div><label className={label}>Genres (comma-sep)</label><input className={field} value={f.genres} onChange={set("genres")} placeholder="Drama, Mystery" data-testid="submit-genres" /></div>
          </div>

          <div>
            <label className={label}>Poster image</label>
            <div className="flex items-center gap-4">
              {f.poster ? (
                <img src={f.poster} alt="poster preview" className="h-28 w-20 flex-none rounded-[var(--card-radius)] border border-[var(--border-subtle)] object-cover" data-testid="submit-poster-preview" />
              ) : (
                <div className="flex h-28 w-20 flex-none items-center justify-center rounded-[var(--card-radius)] border border-dashed border-[var(--border-subtle)] text-[var(--text-muted)]">?</div>
              )}
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-4 py-2.5 text-sm font-semibold text-[var(--text-primary)] hover:border-[var(--accent)]" data-testid="submit-poster-upload">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {uploading ? "Uploading…" : "Upload poster"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadPoster} disabled={uploading} />
              </label>
            </div>
          </div>

          <div>
            <label className={label}>Backdrop image URL (optional)</label>
            <input className={field} value={f.backdrop} onChange={set("backdrop")} placeholder="https://…" data-testid="submit-backdrop" />
          </div>
          <div><label className={label}>Trailer URL (YouTube / Vimeo)</label><input className={field} value={f.trailer_url} onChange={set("trailer_url")} placeholder="https://…" data-testid="submit-trailer" /></div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div><label className={label}>Watch URL *</label><input className={field} value={f.watch_url} onChange={set("watch_url")} required placeholder="https://…" data-testid="submit-watch-url" /></div>
            <div><label className={label}>Watch button label</label><input className={field} value={f.watch_label} onChange={set("watch_label")} placeholder="Watch on …" data-testid="submit-watch-label" /></div>
          </div>

          <button type="submit" disabled={saving || uploading} className="inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-8 py-3 font-bold uppercase tracking-wide text-[var(--accent-contrast)] transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-60" data-testid="submit-button">
            {saving ? <Loader2 size={18} className="animate-spin" /> : null} Submit for review
          </button>
        </form>
        </>)}
      </main>
    </div>
  );
}
